import { AlertTriangle, CheckCircle2, Info, Lock, Mail } from "lucide-react";
import { useId, useState } from "react";

import { useAuth } from "@/modules/auth/hooks/useAuth";
import { ColocaBadge } from "@/shared/components/data-display/ColocaBadge";
import { ColocaSwitch } from "@/shared/components/forms/ColocaSwitch";
import { EmailCodeIllustration } from "@/shared/components/security/EmailCodeIllustration";
import { MethodIllustrationStage } from "@/shared/components/security/MethodIllustrationStage";
import type { SettingsTranslations } from "@/shared/lang/interfaces/settings";
import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";
import { stepUpMethodOf } from "@/shared/utils/stepUp";

import { EmailBackupDialog } from "./EmailBackupDialog";

type BackupState = "none" | "pin_on" | "pin_off" | "totp_on" | "totp_off";

const STATE_COPY: Record<BackupState, { sub: keyof SettingsTranslations; icon: typeof Mail; tone: string }> =
  {
    none: { sub: "security_email_backup_sub_none", icon: Lock, tone: "text-muted-foreground" },
    pin_on: { sub: "security_email_backup_sub_pin_on", icon: CheckCircle2, tone: "text-success" },
    pin_off: { sub: "security_email_backup_sub_pin_off", icon: Info, tone: "text-muted-foreground" },
    totp_on: { sub: "security_email_backup_sub_totp_on", icon: AlertTriangle, tone: "text-warning" },
    totp_off: { sub: "security_email_backup_sub_totp_off", icon: AlertTriangle, tone: "text-warning" },
  };

/**
 * The third option, and visibly the secondary one: not a method but the plan B of the one the member has. On
 * by default with the PIN (a forgotten PIN is its weak spot), off by default with the app, where the card
 * says plainly what turning it on costs. Without a method yet it explains when it becomes available instead
 * of offering a switch. The switch never flips on its own: it opens a dialog that asks for the password.
 */
export function EmailBackupCard({ className }: { className?: string }) {
  const { t } = useTranslations("settings");
  const { user } = useAuth();
  const titleId = useId();
  const [target, setTarget] = useState<boolean | null>(null);

  const method = stepUpMethodOf(user);
  const available = user?.step_up_email_available === true && method !== null;
  const on = available && user?.step_up_email_enabled === true;
  const state: BackupState = !available || !method ? "none" : `${method}_${on ? "on" : "off"}`;
  const copy = STATE_COPY[state];
  const StateIcon = copy.icon;

  return (
    <section
      aria-labelledby={titleId}
      className={cn(
        "animate-fade-up rounded-md border border-dashed p-3.5 transition-colors animate-duration-300 motion-reduce:animate-none",
        on ? "border-primary/35 bg-primary/[0.03]" : "border-border bg-background",
        className,
      )}
    >
      <div className="flex flex-col gap-3.5 sm:flex-row sm:items-center">
        {/* Dimmed while off or unavailable: the envelope still shows what it would do, not that it is doing it. */}
        <MethodIllustrationStage
          className={cn("sm:h-[84px] sm:w-[164px] sm:shrink-0", !on && "opacity-55 grayscale-[0.6]")}
        >
          <EmailCodeIllustration />
        </MethodIllustrationStage>

        <div className="min-w-0 flex-1">
          <div className="font-mono text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            {t("security_email_backup_eyebrow")}
          </div>
          <div className="mt-0.5 flex items-center gap-2">
            <div className="flex min-w-0 flex-1 flex-wrap items-center gap-1.5">
              <Mail size={14} className="shrink-0 text-muted-foreground" aria-hidden="true" />
              <h3 id={titleId} className="text-[13px] font-semibold text-foreground">
                {t("security_email_backup_title")}
              </h3>
              <ColocaBadge variant={on ? "success" : "secondary"}>
                {!available
                  ? t("security_email_backup_tag_unavailable")
                  : on
                    ? t("security_email_backup_tag_on")
                    : t("security_email_backup_tag_off")}
              </ColocaBadge>
            </div>
            <ColocaSwitch
              checked={on}
              disabled={!available}
              onCheckedChange={(next) => setTarget(next)}
              aria-label={t("security_email_backup_switch_label")}
            />
          </div>

          {/* Keyed by the state so a change re-reads as news, not as the same line edited in place. */}
          <p
            key={state}
            className="mt-1.5 flex animate-fade items-start gap-1.5 text-[12px] text-muted-foreground animate-duration-300 motion-reduce:animate-none"
          >
            <StateIcon size={13} className={cn("mt-0.5 shrink-0", copy.tone)} aria-hidden="true" />
            <span>{t(copy.sub)}</span>
          </p>
          {available && user?.email ? (
            <p className="mt-1.5 truncate pl-[19px] text-[11.5px] text-muted-foreground">
              {t("security_email_backup_destination", { email: user.email })}
            </p>
          ) : null}
        </div>
      </div>

      {method ? (
        <EmailBackupDialog
          target={target}
          method={method}
          email={user?.email ?? ""}
          onClose={() => setTarget(null)}
        />
      ) : null}
    </section>
  );
}
